import { API } from "aws-amplify";
import {
  listMyNotifications,
  listAllNotifications,
  getSignedUrl,
} from "../../../graphql/queries";
import {
  createNotification,
  updateNotification,
  deleteNotification,
} from "../../../graphql/mutations";
import {
  type RawNotification,
  type NotificationFormValues,
} from "../models/Notification/notificationTypes";
import { toCreateNotificationInput } from "../models/Notification/notificationMappers";

type ListNotificationsResult = {
  data?: {
    listMyNotifications?: {
      items?: Array<RawNotification | null> | null;
      nextToken?: string | null;
    };
    listAllNotifications?: {
      items?: Array<RawNotification | null> | null;
      nextToken?: string | null;
    };
  };
};

type SaveNotificationResult = {
  data?: {
    createNotification?: RawNotification | null;
    updateNotification?: RawNotification | null;
  };
};

type SignedUrlData = {
  key?: string | null;
  signedUrl?: string | null;
};

type SignedUrlResult = {
  data?: {
    getSignedUrl?: SignedUrlData | null;
  };
};

async function fetchSignedUrl(
  key: string,
  method: "GET" | "PUT",
): Promise<SignedUrlData | null | undefined> {
  const result = (await API.graphql({
    query: getSignedUrl,
    variables: { key, method },
  })) as SignedUrlResult;

  return result.data?.getSignedUrl;
}

function extractItems(
  arr: Array<RawNotification | null> | null | undefined,
): RawNotification[] {
  if (!arr) return [];
  return arr.filter((v): v is RawNotification => v != null);
}

export async function fetchMyNotifications(
  notificationType = "Notification",
): Promise<RawNotification[]> {
  const allItems: RawNotification[] = [];
  let nextToken: string | null | undefined;

  // eslint-disable-next-line no-constant-condition
  do {
    const result = (await API.graphql({
      query: listMyNotifications,
      variables: {
        notificationType,
        ...(nextToken ? { nextToken } : {}),
      },
    })) as ListNotificationsResult;

    const items = extractItems(result.data?.listMyNotifications?.items);
    allItems.push(...items);

    nextToken = result.data?.listMyNotifications?.nextToken;
  } while (nextToken);

  return allItems;
}

export async function fetchAllNotifications(): Promise<RawNotification[]> {
  const allItems: RawNotification[] = [];
  let nextToken: string | null | undefined;

  // eslint-disable-next-line no-constant-condition
  do {
    const result = (await API.graphql({
      query: listAllNotifications,
      variables: {
        ...(nextToken ? { nextToken } : {}),
      },
    })) as ListNotificationsResult;

    const items = extractItems(result.data?.listAllNotifications?.items);
    allItems.push(...items);

    nextToken = result.data?.listAllNotifications?.nextToken;
  } while (nextToken);

  return allItems;
}

export async function saveNotification(
  values: NotificationFormValues,
): Promise<RawNotification> {
  const input = toCreateNotificationInput(values);

  const result = (await API.graphql({
    query: values.id ? updateNotification : createNotification,
    variables: values.id ? { notificationID: values.id, input } : { input },
  })) as SaveNotificationResult;

  const saved =
    result.data?.updateNotification ?? result.data?.createNotification;

  if (!saved) {
    throw new Error("saveNotification: unexpected API response");
  }

  return saved;
}

export async function removeNotification(id: string): Promise<void> {
  await API.graphql({
    query: deleteNotification,
    variables: { notificationID: id },
  });
}

export async function fetchAttachmentSignedUrl(key: string): Promise<string> {
  const data = await fetchSignedUrl(key, "GET");
  if (!data?.signedUrl) {
    throw new Error(
      `fetchAttachmentSignedUrl: no signed URL returned for key "${key}"`,
    );
  }
  return data.signedUrl;
}

export type UploadTarget = {
  key: string;
  signedUrl: string;
};

export async function fetchUploadUrl(fileName: string): Promise<UploadTarget> {
  const data = await fetchSignedUrl(fileName, "PUT");
  if (!data?.key || !data?.signedUrl) {
    throw new Error(
      `fetchUploadUrl: no upload URL returned by the API for "${fileName}"`,
    );
  }
  return { key: data.key, signedUrl: data.signedUrl };
}
